import Vue from 'vue';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import NotificationInboxSvc from '@/services/NotificationInboxSvc';
import { findCompany } from '@/util/companies';
import { isUnread, markReadUpTo, needsCompanySwitch } from '@/util/notificationInbox';
import { escapeHtml } from '@/util/trail/voyageMapExtras';

const LATEST_MAX = 10;
const NOT_FOUND = 404;
const UPDATE_FAILED = "Couldn't update notification.";

// nomor urut request, sengaja di luar state
let countSeq = 0;
let latestSeq = 0;

const emptySeverity = () => ({ CRITICAL: 0, WARNING: 0, INFO: 0 });

// true kalau company item udah aktif atau berhasil diganti
async function enterItemCompany({ dispatch, rootGetters }, item) {
  if (!needsCompanySwitch(item, rootGetters['auth/activeCompany']?.compId)) return true;
  const company = findCompany(rootGetters['auth/availableCompany'], item.compId);
  if (!company) {
    Vue.$toast.error(`You no longer have access to ${escapeHtml(item.compName)}.`);
    return false;
  }
  try {
    await dispatch('auth/switchCompany', company, { root: true });
    return true;
  } catch (error) {
    console.warn('notification company switch failed:', error?.message);
    Vue.$toast.error(`Couldn't switch to ${escapeHtml(item.compName)}.`);
    return false;
  }
}

// halaman yang sama dipakai ulang Vue -> remount biar baca ulang URL
async function pushLink(router, link, dispatch) {
  const target = router.resolve(link).route;
  if (!target.matched.length) {
    console.warn('notification link unknown:', link);
    return;
  }
  const lastRecord = (route) => route.matched[route.matched.length - 1];
  const sameView = lastRecord(target) === lastRecord(router.currentRoute);
  // URL yang tampil, sebelum ditimpa router
  const shownUrl = `${window.location.pathname}${window.location.search}`;
  // URL tujuan sesudah redirect, lengkap dengan base router
  const { href } = router.resolve(target.fullPath);
  try {
    await router.push(link);
  } catch (error) {
    if (!isNavigationFailure(error, NavigationFailureType.duplicated)) {
      if (!isNavigationFailure(error)) console.warn('notification link failed:', error?.message);
      return;
    }
    // URL sempat ditulis ulang halaman lewat replaceState
    if (shownUrl === href) return;
    window.history.replaceState(window.history.state, '', href);
  }
  if (sameView) await dispatch('triggerRemount', null, { root: true });
}

export default {
  namespaced: true,
  state: {
    unread: 0,
    bySeverity: emptySeverity(),
    latest: [],
    latestLoading: false,
    latestError: '',
  },
  getters: {
    unread: (state) => state.unread,
    hasCritical: (state) => state.bySeverity.CRITICAL > 0,
    latest: (state) => state.latest,
    latestLoading: (state) => state.latestLoading,
    latestError: (state) => state.latestError,
  },
  mutations: {
    SET_COUNT(state, { unread, bySeverity }) {
      state.unread = Number(unread) || 0;
      state.bySeverity = { ...emptySeverity(), ...bySeverity };
    },
    SET_LATEST(state, items) {
      state.latest = items;
    },
    SET_LATEST_LOADING(state, value) {
      state.latestLoading = value;
    },
    SET_LATEST_ERROR(state, message) {
      state.latestError = message;
    },
    MARK_LATEST_READ(state, { id, upToId, readAt }) {
      state.latest = markReadUpTo(state.latest, { id, upToId, readAt });
    },
  },
  actions: {
    // true cuma kalau respons request ini yang dipakai
    async fetchCount({ commit }) {
      countSeq += 1;
      const seq = countSeq;
      try {
        const body = await NotificationInboxSvc.getCount();
        if (seq !== countSeq) return false;
        commit('SET_COUNT', body);
        return true;
      } catch (error) {
        // badge tetap angka terakhir
        console.warn('notification count failed:', error?.message);
        return false;
      }
    },

    async fetchLatest({ commit }) {
      latestSeq += 1;
      const seq = latestSeq;
      commit('SET_LATEST_LOADING', true);
      commit('SET_LATEST_ERROR', '');
      try {
        const body = await NotificationInboxSvc.getInbox({ max: LATEST_MAX });
        if (seq === latestSeq) commit('SET_LATEST', body.data || []);
      } catch (error) {
        if (seq !== latestSeq) return;
        console.warn('notification latest failed:', error?.message);
        commit('SET_LATEST_ERROR', "Couldn't load notifications.");
      } finally {
        if (seq === latestSeq) commit('SET_LATEST_LOADING', false);
      }
    },

    // hasilnya waktu baca, null kalau gagal
    async markRead({ commit }, item) {
      try {
        await NotificationInboxSvc.markRead(item.id);
      } catch (error) {
        if (error?.errorCode !== NOT_FOUND) {
          console.warn('notification mark read failed:', error?.message);
          Vue.$toast.error(UPDATE_FAILED);
          return null;
        }
      }
      const readAt = new Date().toISOString();
      commit('MARK_LATEST_READ', { id: item.id, readAt });
      return readAt;
    },

    async markAllRead({ commit, dispatch }, beforeId) {
      if (!beforeId) return null;
      try {
        await NotificationInboxSvc.markAllRead(beforeId);
      } catch (error) {
        console.warn('notification mark all read failed:', error?.message);
        Vue.$toast.error(UPDATE_FAILED);
        return null;
      }
      const readAt = new Date().toISOString();
      commit('MARK_LATEST_READ', { upToId: beforeId, readAt });
      dispatch('fetchCount');
      return readAt;
    },

    // klik item: baca, ganti company, buka link
    async openItem(context, { item, router }) {
      const readAt = isUnread(item) ? await context.dispatch('markRead', item) : null;
      const ready = await enterItemCompany(context, item);
      if (ready && item.link) await pushLink(router, item.link, context.dispatch);
      context.dispatch('fetchCount');
      return readAt;
    },

    // link lepas (mis. View all) lewat jalur yang sama dengan klik item
    openLink({ dispatch }, { link, router }) {
      return pushLink(router, link, dispatch);
    },
  },
};
